import { useState } from "react";
import { User, Mail, Phone, MapPin, Calendar, IdCard, Sparkles, Edit2, X, Save, Loader2, ShieldAlert, Heart } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/config";
import NotificationOptIn from "@/components/NotificationOptIn";
import PhoneInputFr from "@/components/PhoneInputFr";

const RELATION_OPTIONS = [
  { value: "conjoint", label: "Conjoint(e)" },
  { value: "parent", label: "Parent" },
  { value: "enfant", label: "Enfant" },
  { value: "fratrie", label: "Frère / Sœur" },
  { value: "ami", label: "Ami(e)" },
  { value: "autre", label: "Autre" },
];

/**
 * Mobile profile screen.
 *
 * Improvements (iteration 27):
 *  - Bigger font sizes (text-sm / text-base) — the previous UI used text-xs
 *    which was too small on mobile.
 *  - Removed the redundant "Se déconnecter" button — logout is now handled
 *    exclusively via the header ⋮ menu with confirmation to prevent
 *    accidental disconnects.
 *  - Added emergency contact block (name / phone / relation) with save.
 */
export default function ProfileScreen() {
  const { user, checkAuth } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    phone: user?.phone || "",
    address: user?.address || "",
    postal_code: user?.postal_code || "",
    city: user?.city || "",
    disponibilite_debut: user?.disponibilite_debut || "",
    disponibilite_fin: user?.disponibilite_fin || "",
    mobility_km: user?.mobility_km || "",
    emergency_contact_name: user?.emergency_contact_name || "",
    emergency_contact_phone: user?.emergency_contact_phone || "",
    emergency_contact_relation: user?.emergency_contact_relation || "",
  });

  if (!user) return null;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        ...form,
        mobility_km: form.mobility_km ? Number(form.mobility_km) : null,
      }, { withCredentials: true });
      toast.success("Profil mis à jour");
      await checkAuth?.();
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const relationLabel = RELATION_OPTIONS.find((r) => r.value === user.emergency_contact_relation)?.label || user.emergency_contact_relation || "";

  return (
    <div className="p-4 space-y-4 text-sm" data-testid="profile-screen">
      <div className="flex items-center gap-2">
        <User className="w-5 h-5 text-[#662483]" />
        <h1 className="text-xl font-bold text-[#3F3F3F]">Mon profil</h1>
        {!editing && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto rounded-none text-sm h-9 px-4"
            onClick={() => setEditing(true)}
            data-testid="profile-edit-btn"
          >
            <Edit2 className="w-4 h-4 mr-1.5" /> Modifier
          </Button>
        )}
      </div>

      {/* Identity card */}
      <div className="bg-gradient-to-br from-[#662483] to-[#0069B4] text-white p-5 -mx-4">
        <p className="text-xs uppercase tracking-wider opacity-75 mb-1 font-semibold">Candidat</p>
        <p className="text-xl font-bold">{user.name}</p>
        <p className="text-sm opacity-90 mt-1.5 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{user.email}</p>
        {user.phone && <p className="text-sm opacity-90 mt-1 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{user.phone}</p>}
      </div>

      {/* Editable fields */}
      {editing ? (
        <div className="bg-white border border-gray-200 p-4 space-y-3" data-testid="profile-edit-form">
          <div>
            <Label className="text-sm font-semibold text-gray-700 mb-1 block">Téléphone</Label>
            <PhoneInputFr value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} inputClassName="text-base h-11" data-testid="profile-phone" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-gray-700 mb-1 block">Adresse</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none text-base h-11" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-1 block">Code postal</Label>
              <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="rounded-none text-base h-11" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-1 block">Ville</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none text-base h-11" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-1 block">Dispo à partir de</Label>
              <Input type="date" value={form.disponibilite_debut} onChange={(e) => setForm({ ...form, disponibilite_debut: e.target.value })} className="rounded-none text-base h-11" data-testid="profile-dispo-debut" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-1 block">Dispo jusqu&apos;au</Label>
              <Input type="date" value={form.disponibilite_fin} onChange={(e) => setForm({ ...form, disponibilite_fin: e.target.value })} className="rounded-none text-base h-11" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold text-gray-700 mb-1 block">Mobilité max (km)</Label>
            <Input type="number" value={form.mobility_km} onChange={(e) => setForm({ ...form, mobility_km: e.target.value })} className="rounded-none text-base h-11" />
          </div>

          {/* Emergency contact — new in iteration 27 */}
          <div className="border-t border-gray-200 pt-3 mt-2">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="w-4 h-4 text-red-600" />
              <p className="text-sm font-bold text-[#3F3F3F]">Contact d&apos;urgence</p>
              <span className="text-xs text-gray-400 ml-auto">Optionnel</span>
            </div>
            <div className="space-y-2">
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-1 block">Nom et prénom</Label>
                <Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} placeholder="ex : Marie DUPONT" className="rounded-none text-base h-11" data-testid="profile-emergency-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-1 block">Téléphone</Label>
                  <PhoneInputFr value={form.emergency_contact_phone} onChange={(v) => setForm({ ...form, emergency_contact_phone: v })} placeholder="06..." inputClassName="text-base h-11" data-testid="profile-emergency-phone" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-1 block">Lien</Label>
                  <Select value={form.emergency_contact_relation} onValueChange={(v) => setForm({ ...form, emergency_contact_relation: v })}>
                    <SelectTrigger className="rounded-none text-base h-11" data-testid="profile-emergency-relation">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {RELATION_OPTIONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={save}
              disabled={saving}
              className="flex-1 rounded-none bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold h-11 text-base"
              data-testid="profile-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? "..." : "Enregistrer"}
            </Button>
            <Button variant="outline" onClick={() => setEditing(false)} className="rounded-none h-11 px-4" data-testid="profile-cancel-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 p-4 space-y-2.5 text-sm" data-testid="profile-view">
          <div className="flex items-center gap-2 text-gray-700">
            <MapPin className="w-4 h-4 text-[#662483] shrink-0" />
            <span>{user.address ? `${user.address}, ` : ""}{user.postal_code} {user.city || "—"}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-700">
            <Calendar className="w-4 h-4 text-[#662483] shrink-0" />
            <span>Dispo : {user.disponibilite_debut ? new Date(user.disponibilite_debut).toLocaleDateString("fr-FR") : "—"}{user.disponibilite_fin ? ` → ${new Date(user.disponibilite_fin).toLocaleDateString("fr-FR")}` : ""}</span>
          </div>
          {user.mobility_km && (
            <div className="flex items-center gap-2 text-gray-700">
              <MapPin className="w-4 h-4 text-[#662483] shrink-0" />
              <span>Mobilité : {user.mobility_km} km</span>
            </div>
          )}
          {user.id_doc_type && (
            <div className="flex items-center gap-2 text-gray-700">
              <IdCard className="w-4 h-4 text-[#662483] shrink-0" />
              <span>Pièce : {user.id_doc_type.toUpperCase()}{user.id_doc_validity_end ? ` (valide jusqu'au ${new Date(user.id_doc_validity_end).toLocaleDateString("fr-FR")})` : ""}</span>
            </div>
          )}
          {user.nationality && (
            <div className="flex items-center gap-2 text-gray-700">
              <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Nationalité</span>
              <span>{user.nationality}</span>
            </div>
          )}
        </div>
      )}

      {/* Emergency contact — read-only view */}
      {!editing && (user.emergency_contact_name || user.emergency_contact_phone) && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4" data-testid="profile-emergency-view">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-4 h-4 text-red-600" />
            <p className="text-sm uppercase tracking-wider text-red-700 font-bold">Contact d&apos;urgence</p>
          </div>
          <p className="text-base font-semibold text-[#3F3F3F]">{user.emergency_contact_name || "—"}</p>
          <p className="text-sm text-gray-700 mt-0.5 flex items-center gap-1.5">
            <Phone className="w-3.5 h-3.5" />
            {user.emergency_contact_phone || "—"}
            {relationLabel && <span className="text-gray-400 ml-1">· {relationLabel}</span>}
          </p>
        </div>
      )}

      {/* Notifications push — J-1 mission reminders */}
      {!editing && <NotificationOptIn variant="card" testId="profile-notif" />}

      {/* Preferences */}
      {(user.job_preferences || []).length > 0 && (
        <div className="bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#662483]" />
            <p className="text-xs uppercase tracking-wider text-gray-600 font-bold">Métiers recherchés</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(user.job_preferences || []).map((p) => (
              <Badge key={p} className="rounded-none text-xs bg-[#F0E9F3] text-[#662483] px-2 py-1">{p}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
